import { Injectable, Injector } from '@angular/core';
import { LBSErrorHandler, LBSErrorHandlerResult } from '../error-handler';
import { LoggingService } from '../../logging/logic/logging.service';
import { NetworkService } from '../../network/logic/network.service';
import { ImsError } from 'src/app/_util/ims-error';

/**
 * Catches low level network / connection errors (e.g. the native
 * "Software caused connection abort" SocketException that Android throws when
 * the device loses its internet connection while a request is in flight) and
 * turns them into a helpful, translated hint instead of showing the raw and
 * cryptic native error message to the user.
 */
@Injectable({
  providedIn: 'root',
})
export class ConnectionErrorHandler implements LBSErrorHandler {
  // Lowercase substrings of native / browser connection errors that are not
  // helpful for the user. These typically mean the device has no (stable)
  // internet connection and the request never reached (or could not finish
  // talking to) the server.
  private static readonly CONNECTION_ERROR_SIGNATURES: string[] = [
    'software caused connection abort',
    'connection abort',
    'connection reset',
    'connection refused',
    'unable to resolve host',
    'failed to connect',
    'network is unreachable',
    'no address associated with hostname',
    'connect timed out',
    'err_internet_disconnected',
    'err_connection',
    'err_network',
    'err_name_not_resolved',
    'the internet connection appears to be offline',
    'could not connect to the server',
    'a data connection is not currently allowed',
    'the network connection was lost',
    'the request timed out',
  ];

  // Lifecycle
  //
  // WICHTIG: Nur leichtgewichtige Dependencies (Logger, Injector) im Konstruktor!
  // Dieser Handler wird als LBSErrorHandler-Multi-Provider vom globalen
  // ErrorHandler (ImsErrorHandler) EAGER beim Bootstrap instanziiert. Würden wir
  // hier NetworkService direkt injizieren, zöge das über AppParameterService die
  // AuthenticationService/RemoteActionService/CommunicationService-Kette in die
  // ErrorHandler-Erzeugung -> DI-Zirkularität -> App bleibt im Splashscreen
  // hängen. Deshalb wird NetworkService lazy erst im Fehlerfall aufgelöst.
  constructor(
    private logger: LoggingService,
    private injector: Injector,
  ) {}

  public async handleError(error: Error): Promise<LBSErrorHandlerResult> {
    if (!this.isConnectionError(error)) {
      return undefined;
    }

    this.logger.warn(
      this.constructor.name,
      '- caught connection error, showing offline hint instead of raw message:',
      error?.message,
    );

    return {
      alert: {
        title: 'ERROR-HANDLERS.CONNECTION-ERRORS.TITLE',
        subtitle: 'ERROR-HANDLERS.CONNECTION-ERRORS.SUBTITLE',
        message: 'ERROR-HANDLERS.CONNECTION-ERRORS.MESSAGE',
        messageTranslate: true,
        confirm: 'ERROR-HANDLERS.CONNECTION-ERRORS.CONFIRM',
        confirmFunc: () => {},
        showExportLog: false,
      },
    };
  }

  // Private Functions
  private isConnectionError(error: any): boolean {
    if (error == undefined) {
      return false;
    }

    // App level timeouts already have a dedicated, well worded message
    // (IMSERROR.TIMEOUT) -> leave them to the RestErrorHandler / ImsError.
    if (error instanceof ImsError) {
      return false;
    }

    // A well formed HTTP error response with a real status code means the
    // server was reachable -> this is not a pure connection problem, let the
    // RestErrorHandler deal with it.
    if (error?.status != undefined && error?.status !== 0) {
      return false;
    }

    const haystacks: string[] = [];
    if (typeof error === 'string') {
      haystacks.push(error);
    }
    if (typeof error?.message === 'string') {
      haystacks.push(error.message);
    }
    if (typeof error?.error === 'string') {
      haystacks.push(error.error);
    }
    if (typeof error?.error?.message === 'string') {
      haystacks.push(error.error.message);
    }

    const normalized = haystacks.join(' ').toLowerCase();
    const matchesSignature =
      ConnectionErrorHandler.CONNECTION_ERROR_SIGNATURES.some(
        (signature) => normalized.indexOf(signature) >= 0,
      );
    if (matchesSignature) {
      return true;
    }

    // A status 0 response is the browser / native way of signalling that the
    // request never reached the server. Combined with a known offline network
    // state this is almost certainly a connection problem.
    if (
      error?.status === 0 &&
      this.injector.get(NetworkService).connection$.getValue() === false
    ) {
      return true;
    }

    return false;
  }
}
